// The customizer's design state, out of CustomizerScreen (backfill PR 25): the design a new
// visitor starts with and its version, the values a keycap starts with, the write of a change
// at dotted paths, as updateDesign applies every change the screen and the editors make to
// the design, and the undo stack's size limit.
export const CURRENT_CUSTOMIZER_VERSION = '1.1'; // the design format this customizer writes (metadata.customizerVersion); customizerCompat.js migrates older stored designs up to it (it held the constant until PR 25)

export const DEFAULT_DESIGN_STATE = {
  name: '',
  keycapData: [],
  keycapTags: [],
  layoutEditor: {
    id: '', // id of the selected layout document
  },
  profileEditor: {
    profile: '',
  },
  keycapColorEditor: {
    colors: [],
    selectedColorEntry: null,
  },
  textEditor: {
    colors: [],
    selectedColorEntry: null,
    tags: [],
    selectedTag: null,
    selectedTextIndex: null,
    predefinedLegends: {
      id: '', // id of the selected legends document
      os: 'windows',
      configurationStyle: 'full',
      legendStyle: 'text',
    },
  },
  iconEditor: {
    colors: [],
    selectedColorEntry: null,
    tags: [],
    selectedTag: null,
    selectedIconIndex: null,
    activeIconLibraryTab: 'built-in',
  },
  imageEditor: {
    fileName: '',
    dataUrl: null,
    position: { x: 0, y: 0 },
    dimensions: { width: 0, height: 0 },
    rotation: 0,
    keepAspectRatio: true,
    aspectRatio: 1,
  },
  settings: {
    selectedEditor: 'layout',
    selectedKeycaps: [],
    isoEnterSelected: false,
    showGrid: true,
    showBoundingBox: true,
    showPrintableArea: true,
  },
  metadata: {
    customizerVersion: CURRENT_CUSTOMIZER_VERSION,
    isCustomLayout: false,
  },
}; // the design a new visitor starts with: the customizer takes it when nothing is stored, when the stored design's layout is gone and when the layout changes, and a reset takes its editor sections; every use copies it first (deepCopy), as it lives for the whole process and becomes React state

export const defaultKeycapValues = Object.freeze({
  color: '#FFFFFF',
  selectedKeycapColorIndex: null,
  texts: Object.freeze({}),
  icons: Object.freeze({}),
  tagId: null,
}); // what a keycap holds before the user changes it: the customizer gives every keycap of a new design a deep copy (the nested texts and icons must not be shared), and both screens merge a loaded design's keycaps over one, as a saved design stores only what differs from these and the layout (ProcessOrderScreen held an identical copy until PR 25). Frozen with its texts and icons: it lives for the whole process now and every caller copies it, so an in-place write throws instead of reaching every keycap made after it

const MAX_STACK_SIZE = 50; // the number of elements that can be added to the undoStack

export function pushSnapshot(undoStack, designCopy) {
  const newUndoStack = [...undoStack, designCopy]; // Add current design state to undo stack
  while (newUndoStack.length > MAX_STACK_SIZE) {
    newUndoStack.shift(); // Ensure undo stack size is within limit
  }
  return newUndoStack;
} // the undo stack with the design's copy appended and the oldest entries dropped beyond MAX_STACK_SIZE (undoChange and redoChange move an entry between the undo and redo stacks without the limit: together the two never hold more)

export function applyDesignUpdates(prevDesign, updates) {
  if (typeof updates === 'function') {
    // If updates is a function, compute the entire new design state
    const newDesign = updates(prevDesign);
    return newDesign; // Return the computed new design state
  }

  // Otherwise, treat updates as an object with key-value pairs
  const newDesign = { ...prevDesign }; // Create a copy of the previous state

  Object.entries(updates).forEach(([path, newValue]) => {
    const keys = path.split('.'); // Split the path into keys
    const lastKey = keys.pop(); // Get the last key to update
    let currentLevel = newDesign;

    keys.forEach((key) => {
      if (!(key in currentLevel)) {
        throw new Error(`Invalid path: ${path}`);
      }
      currentLevel[key] = { ...currentLevel[key] }; // Ensure immutability at this level
      currentLevel = currentLevel[key];
    });

    currentLevel[lastKey] =
      typeof newValue === 'function'
        ? newValue(currentLevel[lastKey]) // Call function with previous state if value is a function
        : newValue;
  });

  return newDesign; // Return the updated state
} // the design after a change: the result of updates when it is a function, else every value written at its dotted path with the objects along the path copied (a function value is called with the old value); a path whose parent is missing throws: every path the screen and the editors write starts at a section the design has had since its current structure (Nov 2024, before the versions customizerCompat.js migrates), so that is a typo in a path, and the throw runs inside React's updater (eagerly when no update is queued, else during the next render)
